/**
 * Helpers for the Ricerca* search views.
 * @module helpers/SearchHelper
 */

const MAX_SEED = 2147483647; // 2**31 - 1

/**
 * A random seed for seededOrder. Client-only: calling this during render would
 * make the server and the first client render disagree.
 * @returns {number} Integer in [1, 2**31 - 1].
 */
export const makeSeed = () => Math.floor(Math.random() * MAX_SEED) + 1;

/**
 * Deterministic 32-bit hash of a string, mixed with a seed.
 * FNV-1a followed by an xmur3 avalanche, so sequential ids land far apart.
 * @param {string} str Value to hash.
 * @param {number} seed Seed.
 * @returns {number} Unsigned 32-bit integer.
 */
export const hashSeeded = (str, seed) => {
  const value = String(str);
  let h = (2166136261 ^ seed) >>> 0;

  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }

  h ^= h >>> 16;
  h = Math.imul(h, 2246822507);
  h ^= h >>> 13;
  h = Math.imul(h, 3266489909);
  h ^= h >>> 16;

  return h >>> 0;
};

/**
 * Shuffle deterministically: same items + same seed always give the same order.
 *
 * Each item is keyed on a hash of its own id rather than on its position, so the
 * order survives the catalog returning items in a different sequence, and adding
 * or removing an item leaves every other item's relative position untouched.
 * That is what lets a restored page show the same cards.
 *
 * @param {Array} items Items to order.
 * @param {number|null} seed Seed; when null the input order is kept (SSR-safe).
 * @param {string} idKey Property holding a stable unique id.
 * @returns {Array} A new array.
 */
export const seededOrder = (items, seed, idKey = '@id') => {
  const list = items || [];

  if (seed == null) return [...list];

  return list
    .map((item, index) => {
      const id = String(item?.[idKey] ?? index);
      return { item, id, key: hashSeeded(id, seed) };
    })
    .sort((a, b) => a.key - b.key || a.id.localeCompare(b.id))
    .map((entry) => entry.item);
};

const includesToken = (candidate, values) =>
  values.some((value) => String(value) === String(candidate));

/**
 * Does a catalog field match any of the selected vocabulary tokens?
 * Handles the three shapes these views deal with: an array of {token}, a single
 * {token}, and a plain scalar.
 * @param {*} field Field value from the catalog item.
 * @param {Array} values Selected tokens.
 * @returns {boolean} True when the field matches.
 */
export const tokenMatch = (field, values) => {
  if (field == null || !values?.length) return false;

  if (Array.isArray(field)) {
    return field.some((entry) => tokenMatch(entry, values));
  }

  if (typeof field === 'object') {
    return field.token != null && includesToken(field.token, values);
  }

  return includesToken(field, values);
};

/**
 * Is a filter entry actually narrowing anything?
 * @param {*} value Filter entry: an array of tokens or a text string.
 * @returns {boolean} True when the entry should be applied.
 */
export const hasValue = (value) =>
  Array.isArray(value) ? value.length > 0 : value != null && value !== '';

/**
 * Build a checkbox option list out of the result set.
 * @param {Array} items Items to read from.
 * @param {Function} accessor Maps an item to its option value.
 * @param {Object} options Options.
 * @param {string} options.sort 'asc', 'desc' or 'none'.
 * @returns {Array} Unique, non-empty option values.
 */
export const uniqueOptions = (items, accessor, { sort = 'asc' } = {}) => {
  const values = [];

  (items || []).forEach((item) => {
    const value = accessor(item);
    if (value == null || value === '') return;
    if (!values.includes(value)) values.push(value);
  });

  if (sort === 'none') return values;

  const direction = sort === 'desc' ? -1 : 1;

  return values.sort(
    (a, b) =>
      direction * String(a).localeCompare(String(b), 'it', { numeric: true }),
  );
};
